import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../admin.css';
import portfolioMapData from '../../work_list/portfolioMap.json';

interface ExperienceEntry {
    id: string;
    type: string;
    organisation: string;
    role: string;
    begin: string;
    end: string;
    description: string;
    descriptionVersions?: Record<string, string>;
    hiddenByDefault?: boolean;
    relatedWorks: string[];
    tags: string[];
}

const slugify = (value: string): string =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9一-鿿]+/g, '_')
        .replace(/^_+|_+$/g, '') || `entry_${Date.now()}`;

interface ExperienceData {
    typeOrder: string[];
    entries: ExperienceEntry[];
}

interface ExperienceEditorProps {
    initialContent: string;
    onContentChange: (newContent: string) => void;
}

const safeParseJson = (jsonString: string) => {
    try {
        return JSON.parse(jsonString);
    } catch (e) {
        return null;
    }
};

const safeStringifyJson = (data: any) => {
    try {
        return JSON.stringify(data, null, 2);
    } catch (e) {
        return '{}';
    }
};

const TagsInput: React.FC<{
    tags: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
}> = ({ tags, onChange, placeholder }) => {
    const [val, setVal] = useState(tags.join(', '));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) {
            setVal(tags.join(', '));
        }
    }, [tags, focused]);

    return (
        <input
            className="editor-input"
            value={focused ? val : tags.join(', ')}
            onChange={e => {
                setVal(e.target.value);
                onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean));
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
        />
    );
};

const ExperienceEditor: React.FC<ExperienceEditorProps> = ({ initialContent, onContentChange }) => {
    const [data, setData] = useState<ExperienceData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const portfolioMap = portfolioMapData as Record<string, string>;
    const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
    const lastSyncedContentRef = useRef<string>('');

    useEffect(() => {
        // Prevent re-parsing if content hasn't actually changed
        if (initialContent === lastSyncedContentRef.current) return;

        const parsed = safeParseJson(initialContent);
        if (parsed && Array.isArray(parsed.entries) && Array.isArray(parsed.typeOrder)) {
            setData(parsed);
            setError(null);
            lastSyncedContentRef.current = initialContent;
        } else {
            setData(null);
            setError('Invalid Experience Data Structure');
        }
    }, [initialContent]);

    const handleDataChange = useCallback((newData: ExperienceData) => {
        setData(newData);
        onContentChange(safeStringifyJson(newData));
    }, [onContentChange]);

    const handleEntryChange = (index: number, field: keyof ExperienceEntry, value: any) => {
        if (!data) return;
        const newEntries = [...data.entries];
        newEntries[index] = { ...newEntries[index], [field]: value };
        handleDataChange({ ...data, entries: newEntries });
    };

    const handleArrayFieldChange = (index: number, field: 'tags', arrayValue: string[]) => {
        handleEntryChange(index, field, arrayValue);
    };

    const toggleArrayItem = (index: number, field: 'relatedWorks', item: string) => {
        if (!data) return;
        const entry = data.entries[index];
        const currentList = entry[field] || [];
        let newList;
        if (currentList.includes(item)) {
            newList = currentList.filter(i => i !== item);
        } else {
            newList = [...currentList, item];
        }
        handleEntryChange(index, field, newList);
    };

    const addEntry = () => {
        if (!data) return;
        const newEntry: ExperienceEntry = {
            id: `entry_${Date.now()}`,
            type: "Work Experience",
            organisation: "New Organisation",
            role: "New Role",
            begin: new Date().getFullYear().toString(),
            end: "",
            description: "",
            relatedWorks: [],
            tags: []
        };
        const newEntries = [newEntry, ...data.entries];
        handleDataChange({ ...data, entries: newEntries });
        setExpandedIndices([0, ...expandedIndices.map(i => i + 1)]);
    };

    const duplicateEntry = (index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!data) return;
        const entryToCopy = JSON.parse(JSON.stringify(data.entries[index]));
        entryToCopy.id = `${entryToCopy.id || 'entry'}_copy_${Date.now()}`;
        // Make sure to add it right after the original or at top? Let's add after.
        const newEntries = [...data.entries];
        newEntries.splice(index + 1, 0, entryToCopy);
        handleDataChange({ ...data, entries: newEntries });
        setExpandedIndices([...expandedIndices, index + 1]);
    };

    const removeEntry = (index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!data) return;
        if (!window.confirm("Are you sure you want to delete this entry?")) return;
        const newEntries = data.entries.filter((_, i) => i !== index);
        handleDataChange({ ...data, entries: newEntries });
        setExpandedIndices(expandedIndices.filter(i => i !== index).map(i => i > index ? i - 1 : i));
    };

    const toggleExpand = (index: number) => {
        if (expandedIndices.includes(index)) {
            setExpandedIndices(expandedIndices.filter(i => i !== index));
        } else {
            setExpandedIndices([...expandedIndices, index]);
        }
    };

    // Auto-sort by Date (Desc) for JSON organization
    const autoSortJson = () => {
        if (!data) return;
        const sortedEntries = [...data.entries].sort((a, b) => {
            const dateA = new Date(a.begin).getTime() || 0;
            const dateB = new Date(b.begin).getTime() || 0;
            return dateB - dateA;
        });
        handleDataChange({ ...data, entries: sortedEntries });
    };

    const addVersion = (index: number) => {
        if (!data) return;
        const versionName = window.prompt('輸入版本名稱（例如 "data_insight"、"design_focus"）：任何路徑都可以選用這個版本');
        const trimmed = versionName?.trim();
        if (!trimmed) return;
        const entry = data.entries[index];
        const existing = entry.descriptionVersions || {};
        if (existing[trimmed] !== undefined) {
            window.alert(`版本 "${trimmed}" 已經存在了，請直接編輯下方文字。`);
            return;
        }
        handleEntryChange(index, 'descriptionVersions', { ...existing, [trimmed]: entry.description });
    };

    const updateVersion = (index: number, versionName: string, value: string) => {
        if (!data) return;
        const entry = data.entries[index];
        handleEntryChange(index, 'descriptionVersions', { ...(entry.descriptionVersions || {}), [versionName]: value });
    };

    const removeVersion = (index: number, versionName: string) => {
        if (!data) return;
        const entry = data.entries[index];
        if (!window.confirm(`刪除版本 "${versionName}"？`)) return;
        const next = { ...(entry.descriptionVersions || {}) };
        delete next[versionName];
        handleEntryChange(index, 'descriptionVersions', next);
    };

    if (error) return <div className="error-box">{error}</div>;
    if (!data) return <div>Loading...</div>;

    const portfolioKeys = Object.keys(portfolioMap);

    return (
        <div className="editor-container">
            <div className="editor-controls" style={{ justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0 }}>Experience Editor</h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn btn-secondary" onClick={autoSortJson}>Auto Sort JSON by Date</button>
                    <button className="btn btn-primary" onClick={addEntry}>+ Add Entry</button>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {data.entries.map((entry, index) => {
                    const isExpanded = expandedIndices.includes(index);
                    return (
                        <div key={index} className="editor-section" style={{ padding: '0', overflow: 'hidden' }}>
                            <div
                                onClick={() => toggleExpand(index)}
                                style={{
                                    padding: '10px 15px',
                                    background: '#f8f9fa',
                                    borderBottom: isExpanded ? '1px solid #e1e4e8' : 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flex: 1 }}>
                                    <strong style={{ minWidth: '120px', color: '#555' }}>{entry.type}</strong>
                                    <span style={{ fontWeight: 600, fontSize: '1.05em' }}>{entry.organisation}</span>
                                    <span style={{ color: '#666' }}>{entry.role}</span>
                                    {entry.hiddenByDefault && (
                                        <span style={{ color: '#b26a00', background: '#fff3e0', padding: '1px 8px', borderRadius: '10px', fontSize: '0.75em' }}>
                                            預設隱藏
                                        </span>
                                    )}
                                    <span style={{ marginLeft: 'auto', color: '#888', fontSize: '0.9em', marginRight: '15px' }}>
                                        {entry.begin} - {entry.end || 'Present'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                    <button className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '0.8em' }} onClick={(e) => duplicateEntry(index, e)}>Copy</button>
                                    <button className="btn btn-danger" style={{ padding: '2px 8px', fontSize: '0.8em' }} onClick={(e) => removeEntry(index, e)}>Delete</button>
                                </div>
                            </div>

                            {isExpanded && (
                                <div style={{ padding: '1.5rem' }}>
                                    <div className="editor-grid-2">
                                        <div className="editor-col">
                                            <label className="editor-label">ID（路徑要隱藏/套版時用這個識別，需唯一）</label>
                                            <input
                                                className="editor-input"
                                                value={entry.id || ''}
                                                onChange={(e) => handleEntryChange(index, 'id', e.target.value)}
                                                placeholder={slugify(entry.organisation)}
                                                style={{ fontFamily: 'ui-monospace, monospace' }}
                                            />
                                        </div>
                                        <div className="editor-col" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '6px' }}>
                                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(entry.hiddenByDefault)}
                                                    onChange={(e) => handleEntryChange(index, 'hiddenByDefault', e.target.checked)}
                                                />
                                                預設在所有路徑隱藏（個別路徑可再勾選顯示）
                                            </label>
                                        </div>
                                    </div>
                                    <div className="editor-grid-2">
                                        <div className="editor-col">
                                            <label className="editor-label">Type</label>
                                            <select
                                                className="editor-select"
                                                value={entry.type}
                                                onChange={(e) => handleEntryChange(index, 'type', e.target.value)}
                                            >
                                                {data.typeOrder.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <div className="editor-col">
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="editor-label">Begin Date</label>
                                                    <input
                                                        className="editor-input"
                                                        value={entry.begin}
                                                        onChange={(e) => handleEntryChange(index, 'begin', e.target.value)}
                                                        placeholder="YYYY/MM/DD"
                                                    />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="editor-label">End Date</label>
                                                    <input
                                                        className="editor-input"
                                                        value={entry.end}
                                                        onChange={(e) => handleEntryChange(index, 'end', e.target.value)}
                                                        placeholder="YYYY/MM/DD or Present"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="editor-grid-2">
                                        <div className="editor-col">
                                            <label className="editor-label">Organisation</label>
                                            <input
                                                className="editor-input"
                                                value={entry.organisation}
                                                onChange={(e) => handleEntryChange(index, 'organisation', e.target.value)}
                                            />
                                        </div>
                                        <div className="editor-col">
                                            <label className="editor-label">Role</label>
                                            <input
                                                className="editor-input"
                                                value={entry.role}
                                                onChange={(e) => handleEntryChange(index, 'role', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="editor-row">
                                        <label className="editor-label">Description（預設版本）</label>
                                        <textarea
                                            className="editor-textarea"
                                            rows={3}
                                            value={entry.description}
                                            onChange={(e) => handleEntryChange(index, 'description', e.target.value)}
                                        />
                                    </div>

                                    <div className="editor-row">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                            <label className="editor-label" style={{ marginBottom: 0 }}>
                                                已儲存的版本（Saved Versions，任何路徑都可選用）
                                            </label>
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                style={{ padding: '2px 10px', fontSize: '0.8em' }}
                                                onClick={() => addVersion(index)}
                                            >
                                                + 新增版本
                                            </button>
                                        </div>
                                        <div className="editor-help-text" style={{ marginBottom: '8px' }}>
                                            版本不綁定特定路徑。在「Portfolio Routes Editor」的 CV 經歷選擇中，每個路徑可以為這筆經歷自由挑選要用哪個版本，或維持預設 Description。
                                        </div>
                                        {Object.entries(entry.descriptionVersions || {}).length === 0 && (
                                            <div style={{ color: '#999', fontSize: '0.85em' }}>尚無已儲存的版本</div>
                                        )}
                                        {Object.entries(entry.descriptionVersions || {}).map(([versionName, text]) => (
                                            <div
                                                key={versionName}
                                                style={{
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '6px',
                                                    padding: '8px 10px',
                                                    marginBottom: '8px',
                                                    background: '#fafafa',
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                    <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600, fontSize: '0.85em', color: '#1565c0' }}>
                                                        {versionName}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger"
                                                        style={{ padding: '1px 8px', fontSize: '0.75em' }}
                                                        onClick={() => removeVersion(index, versionName)}
                                                    >
                                                        移除
                                                    </button>
                                                </div>
                                                <textarea
                                                    className="editor-textarea"
                                                    rows={3}
                                                    value={text}
                                                    onChange={(e) => updateVersion(index, versionName, e.target.value)}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    <div className="editor-row">
                                        <label className="editor-label">Tags</label>
                                        <TagsInput
                                            tags={entry.tags || []}
                                            onChange={(tags) => handleArrayFieldChange(index, 'tags', tags)}
                                            placeholder="UX, Research, etc."
                                        />
                                    </div>

                                    <div className="editor-row">
                                        <label className="editor-label">Related Works</label>
                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', maxHeight: '150px', overflowY: 'auto', border: '1px solid #eee', padding: '10px', borderRadius: '4px' }}>
                                            {portfolioKeys.map(key => (
                                                <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: entry.relatedWorks.includes(key) ? '#e3f2fd' : '#f8f9fa', padding: '4px 8px', borderRadius: '4px', fontSize: '0.9em', cursor: 'pointer', border: '1px solid #ddd' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={entry.relatedWorks.includes(key)}
                                                        onChange={() => toggleArrayItem(index, 'relatedWorks', key)}
                                                        style={{ display: 'none' }}
                                                    />
                                                    {entry.relatedWorks.includes(key) && <span style={{ color: 'green' }}>✓</span>}
                                                    <span>{key}</span>
                                                    <span style={{ fontSize: '0.8em', color: '#666' }}>({portfolioMap[key]})</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ExperienceEditor;
